/**
 * AnalyticsDashboard - Project analytics and reporting page
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  analyticsApi,
  type ProjectMetrics,
  type DistributionData,
} from '@services/api';
import { toast } from '@components/ui/Toast';
import {
  MetricsCard,
  VelocityChart,
  BurndownChart,
  DistributionChart,
  TeamPerformanceTable,
} from '@components/analytics';

type DateRangeOption = '7d' | '30d' | '90d' | 'all';

export default function AnalyticsDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();

  const [metrics, setMetrics] = useState<ProjectMetrics | null>(null);
  const [statusDistribution, setStatusDistribution] = useState<DistributionData[]>([]);
  const [priorityDistribution, setPriorityDistribution] = useState<DistributionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeOption>('30d');
  const [activeTab, setActiveTab] = useState<'overview' | 'velocity' | 'team'>('overview');

  const dateRangeParams = useMemo((): { startDate: string; endDate: string } | undefined => {
    const now = new Date();
    const endDateStr = now.toISOString().split('T')[0];
    if (!endDateStr) return undefined;

    let startDateStr: string | undefined;

    switch (dateRange) {
      case '7d':
        startDateStr = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '30d':
        startDateStr = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '90d':
        startDateStr = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      default:
        return undefined;
    }

    if (!startDateStr) return undefined;

    return { startDate: startDateStr, endDate: endDateStr };
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(false);
    try {
      const [metricsData, statusData, priorityData] = await Promise.all([
        analyticsApi.getProjectMetrics(projectId),
        analyticsApi.getStatusDistribution(projectId),
        analyticsApi.getPriorityDistribution(projectId),
      ]);

      setMetrics(metricsData);
      setStatusDistribution(statusData);
      setPriorityDistribution(priorityData);
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const handleExport = async (format: 'pdf' | 'csv' | 'xlsx') => {
    if (!projectId) return;

    try {
      const blob = await analyticsApi.exportReport(projectId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${projectId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export analytics report:', err);
      toast.error(t('analytics.exportError', 'Failed to export report. Please try again.'));
    }
  };

  if (!projectId) {
    return (
      <div className="p-6 text-center text-neutral-500">
        {t('analytics.noProject')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4 text-center px-4">
        <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
        </svg>
        <div>
          <p className="text-base font-medium text-neutral-900 dark:text-white">
            {t('analytics.loadError', 'Failed to load analytics')}
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            {t('analytics.loadErrorSubtitle', 'Something went wrong while loading this data.')}
          </p>
        </div>
        <button
          onClick={() => fetchData()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              {t('analytics.title')}
            </h1>
            <p className="mt-1 text-sm text-neutral-500">
              {t('analytics.subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Date range selector */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
              className="px-4 py-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm"
            >
              <option value="7d">{t('analytics.last7Days')}</option>
              <option value="30d">{t('analytics.last30Days')}</option>
              <option value="90d">{t('analytics.last90Days')}</option>
              <option value="all">{t('analytics.allTime')}</option>
            </select>

            {/* Export dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('analytics.export')}
              </button>
              <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  PDF
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  CSV
                </button>
                <button
                  onClick={() => handleExport('xlsx')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-neutral-200 dark:border-neutral-700">
          <nav className="flex gap-8">
            {(['overview', 'velocity', 'team'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                {t(`analytics.tabs.${tab}`)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && metrics && (
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricsCard
              title={t('analytics.healthScore')}
              value={metrics.healthScore}
              format="percentage"
              color={getHealthScoreColor(metrics.healthScore)}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <MetricsCard
              title={t('analytics.completion')}
              value={metrics.totalTasks > 0 ? (metrics.completedTasks / metrics.totalTasks) * 100 : 0}
              format="percentage"
              subtitle={`${metrics.completedTasks}/${metrics.totalTasks} ${t('task.tasks')}`}
              color="info"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              }
            />
            <MetricsCard
              title={t('analytics.avgVelocity')}
              value={metrics.averageVelocity}
              format="points"
              color="primary"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            />
            <MetricsCard
              title={t('analytics.avgCycleTime')}
              value={metrics.averageCycleTime}
              format="duration"
              color="warning"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DistributionChart
              data={statusDistribution}
              title={t('analytics.tasksByStatus')}
              type="donut"
            />
            <DistributionChart
              data={priorityDistribution}
              title={t('analytics.tasksByPriority')}
              type="donut"
            />
          </div>

          {/* Burndown Chart */}
          <BurndownChart projectId={projectId} />
        </div>
      )}

      {/* Velocity Tab */}
      {activeTab === 'velocity' && (
        <div className="space-y-8">
          <VelocityChart projectId={projectId} sprintCount={8} />
          <BurndownChart projectId={projectId} />
        </div>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <div className="space-y-8">
          <TeamPerformanceTable projectId={projectId} {...(dateRangeParams ? { dateRange: dateRangeParams } : {})} />
        </div>
      )}
    </div>
  );
}
